import React, { useState, useReducer } from 'react';

import {
  Button,
  Container,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import PdfEditorTemplateHeader from '@hydra/routes/pdf-editor/components/PdfEditorTemplateHeader';
import { getInitialCustomCriteria, getOverwrittenTemplateForProUser } from '@hydra/routes/pdf-editor/utils/pdfEditorUtils';
import { useUserContext } from '@hydra/common/context';
import TabGeneral from '@hydra/routes/pdf-editor/components/EditorForm/TabGeneral';
import TabFooter from '@hydra/routes/pdf-editor/components/EditorForm/TabFooter';
import TabIntroduction from '@hydra/routes/pdf-editor/components/EditorForm/TabIntroduction';
import TabConclusion from '@hydra/routes/pdf-editor/components/EditorForm/TabConclusion';
import TabSerp from '@hydra/routes/pdf-editor/components/EditorForm/TabSerp';
import TabReview from '@hydra/routes/pdf-editor/components/EditorForm/TabReview';
import TabTraffic from '@hydra/routes/pdf-editor/components/EditorForm/TabTraffic';
import TabAdvanced from '@hydra/routes/pdf-editor/components/EditorForm/TabAdvanced';
import PdfEditorBannerCTA from '@hydra/common/components/CTA/PdfEditorBannerCTA';
import { Translate } from '@hydra/common/components';

import { useI18n } from '@hydra/common/customHooks';

import { getCriteriaModules } from '@hydra/utils/reviewStructureHelper';
import * as fetcher from '@hydra/utils/fetcher';

const useStyles = makeStyles(theme => ({
  paper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5
  },
  tabs: {
    margin: theme.spacing(4, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tab: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightSemibold,
    minWidth: 0
  },
  error: {
    backgroundColor: theme.palette.error.medium,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      color: theme.palette.common.white
    }
  },
  content: { margin: theme.spacing(2, 0) },
  banner: { marginTop: theme.spacing(3) }
}));

const getEditorName = (name, translateFn) => {
  if (!name) {
    return '';
  }

  if (name.includes('customCriteria')) {
    name = 'customCriteria';
  }

  return {
    name: translateFn('pdf_editor_template_name'),
    'colorScheme.primaryColor': translateFn('pdf_editor_primary_color'),
    'colorScheme.secondaryColors': translateFn('pdf_editor_secondary_colors'),
    'colorScheme.footerColor': translateFn('pdf_editor_footer_color'),
    footer: translateFn('pdf_editor_footer_text'),
    logo: translateFn('pdf_editor_logo'),
    footerLogo: translateFn('pdf_editor_footer_logo'),
    coverPage: translateFn('pdf_editor_intro_text'),
    'closingPage.customText': translateFn('pdf_editor_conclusion_text'),
    signature: translateFn('pdf_editor_signature'),
    customCriteria: translateFn('pdf_editor_option_label_advice_text'),
    customOpeningPage: translateFn('pdf_editor_advanced_custom_opening_page'),
    customClosingPage: translateFn('pdf_editor_advanced_custom_closing_page')
  }[name];
};

function TabPanel ({ children, value, index }) {
  return (
    <Typography
      component='div'
      hidden={value !== index}
      id={`tab-panel-${index}`}
    >
      {children}
    </Typography>
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SAVE_FIELD':
      return {
        ...state,
        template: {
          ...action.template,
          customCriteria: getInitialCustomCriteria({ modules: state.modules, initialCustomCriteria: action.template.customCriteria })
        }
      };
    case 'SAVE_IMAGE':
      return {
        ...state,
        template: {
          ...state.template,
          [action.field]: action.res
        }
      };
    case 'SET_EDITING_VALUE':
      return {
        ...state,
        editingValue: action.value
      };
    case 'SET_EDITING_FIELD':
      return {
        ...state,
        editing: action.field
      };
    case 'RESET_EDITING':
      return {
        ...state,
        editing: null,
        editingValue: null
      };
    default:
      return state;
  }
};

const init = (initialData) => ({
  template: {
    ...initialData.template,
    customCriteria: getInitialCustomCriteria({ modules: getCriteriaModules(), initialCustomCriteria: initialData.template.customCriteria })
  },
  modules: getCriteriaModules(),
  editing: null,
  editingValue: null
});

export const PdfEditorForm = ({ data }) => {
  const [{
    template,
    editing,
    editingValue,
    modules
  }, dispatch] = useReducer(reducer, data, init);
  const { user } = useUserContext();
  const isPdfAllowed = user && user.isPdfAllowed;
  const pdfEditorLimited = !isPdfAllowed;

  const { translate, locale } = useI18n();
  const sections = [
    {
      component: TabGeneral,
      label: 'pdf_editor_tab_general',
      headerLocked: false,
      featuresLocked: pdfEditorLimited
    },
    {
      component: TabFooter,
      label: 'pdf_editor_tab_footer',
      headerLocked: pdfEditorLimited,
      featuresLocked: pdfEditorLimited
    },
    {
      component: TabIntroduction,
      label: 'pdf_editor_tab_introduction',
      headerLocked: pdfEditorLimited,
      featuresLocked: pdfEditorLimited
    },
    {
      component: TabConclusion,
      label: 'pdf_editor_review_tab_conclusion',
      headerLocked: pdfEditorLimited,
      featuresLocked: pdfEditorLimited
    },
    {
      component: TabReview,
      label: 'pdf_editor_tab_review',
      headerLocked: false,
      featuresLocked: false
    },
    {
      component: TabSerp,
      label: 'pdf_editor_review_tab_serp',
      headerLocked: pdfEditorLimited,
      featuresLocked: pdfEditorLimited
    },
    {
      component: TabTraffic,
      label: 'pdf_editor_tab_traffic',
      headerLocked: pdfEditorLimited,
      featuresLocked: pdfEditorLimited
    }
  ];

  if (data.showAdvancedTab) {
    sections.push({
      component: TabAdvanced,
      label: 'pdf_editor_review_tab_advanced',
      headerLocked: false,
      featuresLocked: false
    });
  }

  const classes = useStyles();
  const [value, setValue] = useState(0);
  const [nextTab, setNextTab] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (event, newValue) => {
    if (value === newValue) {
      return;
    }
    if (editing) {
      setShowModal(true);
      setNextTab(newValue);
      return;
    }
    setValue(newValue);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleDiscard = () => {
    resetEditing();
    setShowModal(false);
    setValue(nextTab);
  };

  const saveFormField = async ({ field, value }) => {
    await fetcher.put(`/api/pdf-editor/templates/${template.id}`, {
      json: { template: { [field]: value } }
    });
    const newTemplate = await fetcher.get(`/api/pdf-editor/templates/${template.id}`).json();
    dispatch({ type: 'SAVE_FIELD', template: newTemplate });
  };

  const setEditingValue = value => dispatch({ type: 'SET_EDITING_VALUE', value });

  const setEditingField = field => dispatch({ type: 'SET_EDITING_FIELD', field });

  const resetEditing = () => dispatch({ type: 'RESET_EDITING' });

  const savePdfFile = async ({ field, value }) => {
    const isUploading = (value.file instanceof window.File);
    const isDeleting = (value.file === null);

    if (isUploading) {
      const body = new fetcher.FormData();
      body.append('media', value.file);
      const res = await fetcher.post(`/api/pdf-editor/templates/${template.id}/${field}`, {
        body
      }).json();
      dispatch({ type: 'SAVE_IMAGE', res, field });
    } else if (isDeleting) {
      await fetcher.del(`/api/pdf-editor/templates/${template.id}/${field}`).json();
      dispatch({ type: 'SAVE_IMAGE', field, res: null });
    }
  };

  return (
    <>
      <PdfEditorTemplateHeader template={template} language={locale} />
      <Container maxWidth='lg'>
        <Tabs
          value={value}
          indicatorColor='primary'
          onChange={handleChange}
          variant='scrollable'
          className={classes.tabs}
        >
          {
            sections.map(({ label }) => (
              <Tab
                className={classes.tab}
                disableRipple
                key={label}
                label={translate(label) && translate(label).toUpperCase()}
              />
            ))
          }
        </Tabs>
        {
          sections.map((section, index) => (
            <TabPanel key={index} value={value} index={index}>
              <Paper elevation={0} className={classes.paper}>
                <section.component
                  template={pdfEditorLimited ? getOverwrittenTemplateForProUser(template) : template}
                  editing={editing}
                  editingValue={editingValue}
                  saveFormField={saveFormField}
                  setEditingValue={setEditingValue}
                  setEditingField={setEditingField}
                  resetEditing={resetEditing}
                  savePdfFile={savePdfFile}
                  modules={modules}
                  headerLocked={section.headerLocked}
                  featuresLocked={section.featuresLocked}
                  countries={data.countries}
                />
              </Paper>
            </TabPanel>
          ))
        }
        <Dialog
          maxWidth='md'
          onClose={handleCancel}
          open={showModal}
        >
          <DialogTitle>
            <Typography component='h2' variant='h3' align='center'>
              <Translate
                content='pdf_editor_unsaved_changes_modal_title'
                with={{ editor: getEditorName(editing, translate) }}
              />
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Translate
              content='pdf_editor_unsaved_changes_modal_text'
              with={{ editor: getEditorName(editing, translate) }}
              className={classes.content}
            />
          </DialogContent>
          <DialogActions>
            <Button
              label='Cancel'
              variant='contained'
              color='primary'
              onClick={handleCancel}
            >
              <Translate content='pdf_editor_unsaved_changes_modal_go_back' />
            </Button>
            <Button
              label='Discard changes'
              variant='contained'
              className={classes.error}
              onClick={handleDiscard}
            >
              <Translate content='pdf_editor_unsaved_changes_modal_discard' />
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      {pdfEditorLimited && (
        <div className={classes.banner}>
          <PdfEditorBannerCTA />
        </div>
      )}
    </>
  );
};

export default PdfEditorForm;
